import React from 'react';
import { useNavigate } from 'react-router-dom';
import { IconFormIcons, Lock } from 'shared/assets/icons';
import { LifeAreaContextMenu } from 'shared/components/connected/life-area-context-menu';
import { Icon } from 'shared/components/ui/icon';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { Paths } from 'shared/routes/paths';
import { DataType } from 'shared/types/data-type';
import { ID } from 'shared/types/id';
import { replaceUrlParams } from 'shared/utils/replace-url-params';
import { useTheme } from 'styled-components';

import { LifeAreaRowData } from '../../types';
import * as Styled from './life-area-list-card.style';

type CardLinkProps = {
  id: ID;
  title: string;
  isFrozen?: boolean;
  onClick?: (id: ID) => void;
  preventInteraction?: boolean;
  children: React.ReactNode;
};

const CardLink: React.FC<CardLinkProps> = ({
  id,
  title,
  isFrozen,
  onClick,
  preventInteraction,
  children,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dataProps = {
    'data-component-type': DataType.LifeArea,
    'data-id': id,
    'data-title': title,
  };

  if (preventInteraction) {
    return (
      <Styled.ClickContainer {...dataProps}>{children}</Styled.ClickContainer>
    );
  }

  if (isFrozen || onClick) {
    const handleClick = onClick ? () => onClick(id) : undefined;
    return (
      <Styled.ClickContainer {...dataProps} onClick={handleClick}>
        {children}
      </Styled.ClickContainer>
    );
  }

  if (theme.isTouchDevice) {
    const handleClick = () =>
      navigate(replaceUrlParams(Paths.LifeAreaDetail, { id }));
    return (
      <Styled.ClickContainer {...dataProps} onClick={handleClick}>
        {children}
      </Styled.ClickContainer>
    );
  }

  return (
    <Styled.LinkContainer
      {...dataProps}
      to={replaceUrlParams(Paths.LifeAreaDetail, { id })}
    >
      {children}
    </Styled.LinkContainer>
  );
};

export type LifeAreaListCardProps = {
  row: LifeAreaRowData;
  onClick?: (id: ID) => void;
  isFrozen?: boolean;
  isArchived?: boolean;
  isSelected?: boolean;
  isDropTarget?: boolean;
  preventInteraction?: boolean;
  hideContextMenu?: boolean;
};

export const LifeAreaListCard: React.FC<LifeAreaListCardProps> = ({
  row,
  onClick,
  isFrozen,
  isArchived,
  isSelected,
  isDropTarget,
  preventInteraction,
  hideContextMenu,
}) => {
  const theme = useTheme();
  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState({ disabled: isFrozen || hideContextMenu });

  const showSelection = isSelected || !!menuPosition;

  return (
    <>
      <CardLink
        id={row.id}
        title={row.name}
        isFrozen={isFrozen}
        onClick={onClick}
        preventInteraction={preventInteraction}
      >
        <Styled.Container
          onContextMenu={openMenu}
          $isArchived={!!isArchived}
          $isSelected={showSelection}
          $isDropTarget={isDropTarget}
        >
          <Styled.DataContainer
            $type={Styled.DataTypes.Name}
            $isFrozen={!!isFrozen}
          >
            <Styled.IllustrationContainer>
              <Styled.ImageContainer>
                {row.image ? (
                  <Styled.Image
                    src={row.image.url}
                    alt={row.name}
                    hash={row.image.blurHash}
                  />
                ) : (
                  <Icon icon={IconFormIcons[row.iconName]} />
                )}
              </Styled.ImageContainer>

              {!!row.color && <Styled.ColorDot $color={row.color} />}
            </Styled.IllustrationContainer>

            <Styled.Title>{row.name}</Styled.Title>

            {!!isFrozen && (
              <Styled.IconContainer>
                <Icon icon={Lock} />
              </Styled.IconContainer>
            )}
          </Styled.DataContainer>

          <Styled.RightGroup>
            <Styled.DataContainer
              $type={Styled.DataTypes.Vision}
              $isFrozen={!!isFrozen}
            >
              <Styled.OverflowSpan>
                {row.visionDescription || '-'}
              </Styled.OverflowSpan>
            </Styled.DataContainer>

            <Styled.DataContainer
              $type={Styled.DataTypes.Goals}
              $isFrozen={!!isFrozen}
            >
              <Styled.Count $isEmpty={!row.goals}>
                {row.goals || '-'}
              </Styled.Count>
            </Styled.DataContainer>

            <Styled.DataContainer
              $type={Styled.DataTypes.Projects}
              $isFrozen={!!isFrozen}
            >
              <Styled.Count $isEmpty={!row.projects}>
                {row.projects || '-'}
              </Styled.Count>
            </Styled.DataContainer>
          </Styled.RightGroup>
        </Styled.Container>
      </CardLink>

      {!!menuPosition && !theme.isMobile && (
        <LifeAreaContextMenu
          lifeArea={row.lifeArea}
          location={menuPosition}
          position="bottom-start"
          onClose={closeMenu}
        />
      )}
    </>
  );
};
